/**
 * WYVIO Design System — atomes partagés (source unique).
 *
 * Basé sur Home.jsx + emails.py (sources de vérité validées par l'utilisateur).
 * Utilisé par : Dashboard, CommandCenter, DisplayScreen (TV),
 *              CommandCenterScreenPreview, DisplaySettings, DisplayTokensManager,
 *              et tous les futurs espaces (Mandats, Ventes, SAV, Workflows, Protection).
 *
 * PALETTE STRICTE — aucune couleur hors de cette liste :
 *   #FFFFFF · #F5F5F5 · #F7F8FA · #F0F0F0 · #EEF2F5
 *   #111111 · #0A0A0B · #6B7280 · #9CA3AF · #B0B5BC
 *   #C8FF2F · #F0FFDE · #F7FFC9 · #4a6b0f · #8FB600
 *
 * Le vert est RARE — réservé à : KPI principal, CTA principal, statut prêt/validé, sync active.
 * Le noir est ACCENT — max 1 card noire dominante par zone.
 * Le blanc DOMINE toujours.
 */
import { ArrowRight, AlertTriangle, Wifi, WifiOff } from "lucide-react";

// ────────────────────────────────────────────────────────────────────────
// TOKENS
// ────────────────────────────────────────────────────────────────────────

export const WV = {
  page:       "#FFFFFF",
  pageAlt:    "#F5F5F5",
  muted:      "#F7F8FA",
  cardSoft:   "#F8F8F8",
  ink:        "#111111",
  inkSoft:    "#0A0A0B",
  textMuted:  "#6B7280",
  textFaint:  "#9CA3AF",
  line:       "#F0F0F0",
  hairline:   "rgba(0,0,0,0.05)",
  hairlineHi: "rgba(0,0,0,0.08)",
  lime:       "#C8FF2F",
  limeSoft:   "#F7FFC9",
  limeChip:   "#F0FFDE",
  limeChipTx: "#4a6b0f",
  limeDot:    "#8FB600",
};

// ────────────────────────────────────────────────────────────────────────
// EYEBROWS — chips lime et labels uppercase
// ────────────────────────────────────────────────────────────────────────

/** Chip lime (Home ligne 36-42) — pour dater/qualifier une section */
export const Chip = ({ children, dot = true, className = "", testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center gap-2 rounded-full text-[12px] font-medium px-3 py-1.5 ${className}`}
    style={{ background: WV.limeChip, color: WV.limeChipTx }}
  >
    {dot && <span className="h-1.5 w-1.5 rounded-full" style={{ background: WV.limeDot }} />}
    {children}
  </span>
);

/** Chip live (sync active) — pulsant */
export const LiveChip = ({ label = "Sync live", testId }) => (
  <span
    data-testid={testId}
    className="inline-flex items-center gap-2 rounded-full px-3 py-1.5"
    style={{ background: WV.limeChip, color: WV.limeChipTx }}
  >
    <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: WV.limeDot }} />
    <span className="text-[12px] font-medium">{label}</span>
  </span>
);

/** Micro-label uppercase (Home ligne 199 · emails info_card label) */
export const Upper = ({ children, className = "", tone = "muted" }) => (
  <p
    className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${className}`}
    style={{ color: tone === "lime" ? WV.limeDot : WV.textMuted }}
  >
    {children}
  </p>
);

// ────────────────────────────────────────────────────────────────────────
// CARDS — 3 variantes principales + soft
// ────────────────────────────────────────────────────────────────────────

/** Card blanche standard (Home ligne 123) — bordure hairline */
export const Card = ({ children, className = "", pad = "p-6 lg:p-8", testId, onClick, as: As = "div" }) => (
  <As
    data-testid={testId}
    onClick={onClick}
    className={`rounded-2xl ${pad} bg-white ${onClick ? "cursor-pointer" : ""} ${className}`}
    style={{ border: `1px solid ${WV.hairline}` }}
  >
    {children}
  </As>
);

/** Card muted grise (Home ligne 183/198) — pour zones secondaires / grouper */
export const CardSoft = ({ children, className = "", pad = "p-6 lg:p-8", testId, onClick }) => (
  <div
    data-testid={testId}
    onClick={onClick}
    className={`rounded-2xl ${pad} ${onClick ? "cursor-pointer" : ""} ${className}`}
    style={{ background: WV.muted }}
  >
    {children}
  </div>
);

/** Card noire accent avec halo lime (Home ligne 78-116, 219, 245) — MAX 1 par zone */
export const CardDark = ({
  children, className = "", pad = "p-8 lg:p-10", testId,
  halo = true, haloSize = "h-72 w-72", haloPos = "-top-24 -right-24",
}) => (
  <div
    data-testid={testId}
    className={`relative rounded-3xl ${pad} text-white overflow-hidden shadow-2xl shadow-black/10 ${className}`}
    style={{ background: WV.ink }}
  >
    {halo && (
      <div
        aria-hidden
        className={`absolute ${haloPos} ${haloSize} rounded-full blur-3xl pointer-events-none`}
        style={{ background: "rgba(200,255,47,0.20)" }}
      />
    )}
    <div className="relative">{children}</div>
  </div>
);

// ────────────────────────────────────────────────────────────────────────
// BUTTONS — 3 variantes (Home ligne 61, 68, 258)
// ────────────────────────────────────────────────────────────────────────

/** CTA primaire noir plein — sur fond blanc/gris */
export const BtnPrimary = ({ children, onClick, testId, className = "", type = "button", disabled }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={`h-11 inline-flex items-center gap-2 rounded-full text-white text-[13px] font-medium px-5 transition-colors hover:brightness-110 disabled:opacity-50 ${className}`}
    style={{ background: WV.ink }}
  >
    {children}
  </button>
);

/** CTA lime — sur fond noir OU pour statut prêt/validé (usage rare) */
export const BtnLime = ({ children, onClick, testId, className = "", type = "button", disabled }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={`h-11 inline-flex items-center gap-2 rounded-full text-[13px] font-semibold px-5 transition-all hover:brightness-95 disabled:opacity-50 ${className}`}
    style={{ background: WV.lime, color: WV.ink }}
  >
    {children}
  </button>
);

/** CTA secondaire outline noir */
export const BtnGhost = ({ children, onClick, testId, className = "", type = "button" }) => (
  <button
    type={type}
    onClick={onClick}
    data-testid={testId}
    className={`h-11 inline-flex items-center gap-2 rounded-full border text-[13px] font-medium px-5 transition-colors hover:bg-white ${className}`}
    style={{ borderColor: "rgba(0,0,0,0.1)", color: WV.ink, background: "transparent" }}
  >
    {children}
  </button>
);

/** Icon button rond (refresh, close, etc.) */
export const BtnIcon = ({ children, onClick, testId, title, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    data-testid={testId}
    className={`h-11 w-11 rounded-full border bg-white flex items-center justify-center transition-colors hover:bg-[#F7F8FA] ${className}`}
    style={{ borderColor: WV.hairline, color: WV.textMuted }}
  >
    {children}
  </button>
);

// ────────────────────────────────────────────────────────────────────────
// STATUS INDICATORS
// ────────────────────────────────────────────────────────────────────────

/** Dot de statut — palette figée, jamais de rouge/orange inventé */
export const StatusDot = ({ state, size = "h-2 w-2", forceLight = false }) => {
  if (state === "ok" || state === "ready" || state === "validated")
    return <span className={`${size} rounded-full`} style={{ background: WV.lime }} />;
  if (state === "blocked" || state === "critical")
    return <AlertTriangle size={14} strokeWidth={2.25} style={{ color: forceLight ? "#fff" : WV.ink }} />;
  if (state === "progress" || state === "action_required")
    return <span className={`${size} rounded-full border-2`} style={{ borderColor: forceLight ? "#fff" : WV.ink }} />;
  if (state === "attention")
    return <span className={`${size} rounded-full border-2`} style={{ borderColor: forceLight ? "#fff" : WV.ink }} />;
  return <span className={`${size} rounded-full`} style={{ background: forceLight ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.15)" }} />;
};

/** Badge de statut textuel — utilise typographie et bordure, pas la couleur */
export const StatusBadge = ({ state, label }) => {
  const map = {
    ready:            { bg: WV.limeChip,  fg: WV.limeChipTx },
    ok:               { bg: WV.limeChip,  fg: WV.limeChipTx },
    validated:        { bg: WV.limeChip,  fg: WV.limeChipTx },
    action_required:  { bg: WV.muted,     fg: WV.ink },
    attention:        { bg: WV.muted,     fg: WV.ink },
    progress:         { bg: WV.muted,     fg: WV.ink },
    blocked:          { bg: WV.ink,       fg: "#fff" },
    critical:         { bg: WV.ink,       fg: "#fff" },
  };
  const s = map[state] || { bg: WV.muted, fg: WV.textMuted };
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full text-[11px] font-semibold px-2.5 py-1"
      style={{ background: s.bg, color: s.fg }}
    >
      {state === "blocked" || state === "critical" ? <AlertTriangle size={10} strokeWidth={2.5} /> : null}
      {label}
    </span>
  );
};

// ────────────────────────────────────────────────────────────────────────
// TYPOGRAPHIC ATOMS
// ────────────────────────────────────────────────────────────────────────

/** Hero title éditorial (Home ligne 44) */
export const HeroTitle = ({ children, className = "", testId }) => (
  <h1
    data-testid={testId}
    className={`font-semibold text-[#111] leading-[1.05] ${className}`}
    style={{ letterSpacing: "-0.03em", fontSize: "clamp(32px, 5vw, 56px)" }}
  >
    {children}
  </h1>
);

/** Sous-titre gris muted */
export const HeroLead = ({ children, className = "" }) => (
  <p className={`text-[15px] lg:text-[17px] leading-[1.6] ${className}`} style={{ color: WV.textMuted }}>
    {children}
  </p>
);

/** Section H2 */
export const SectionTitle = ({ children, className = "" }) => (
  <h2 className={`text-[19px] lg:text-[22px] font-semibold tracking-tight ${className}`} style={{ color: WV.ink }}>
    {children}
  </h2>
);

// ────────────────────────────────────────────────────────────────────────
// BIG NUMBER — chiffre qui domine, label en bas discret
// ────────────────────────────────────────────────────────────────────────

/** KPI standard : chiffre 56-64px + label 12px muted */
export const BigNumber = ({ n, label, accent = false, size = "md", testId }) => {
  const sz = {
    sm: { num: 40, gap: 12 },
    md: { num: 64, gap: 16 },
    lg: { num: 96, gap: 20 },
    xl: { num: 140, gap: 24 },
  }[size] || { num: 64, gap: 16 };
  return (
    <div data-testid={testId}>
      <p
        className="font-semibold tabular-nums leading-[0.9]"
        style={{
          color: accent ? WV.lime : WV.ink,
          fontSize: `${sz.num}px`,
          letterSpacing: "-0.035em",
        }}
      >
        {n}
      </p>
      <p
        className="font-semibold uppercase"
        style={{
          color: WV.textMuted,
          fontSize: "11px",
          letterSpacing: "0.14em",
          marginTop: `${sz.gap}px`,
        }}
      >
        {label}
      </p>
    </div>
  );
};

/** BigNumber sur card noire — accent lime possible */
export const BigNumberDark = ({ n, label, accent = false, size = "md", testId }) => {
  const sz = {
    sm: { num: 40, gap: 12 },
    md: { num: 64, gap: 16 },
    lg: { num: 96, gap: 20 },
    xl: { num: 140, gap: 24 },
  }[size] || { num: 64, gap: 16 };
  return (
    <div data-testid={testId}>
      <p
        className="font-semibold tabular-nums leading-[0.9]"
        style={{
          color: accent ? WV.lime : "#fff",
          fontSize: `${sz.num}px`,
          letterSpacing: "-0.035em",
        }}
      >
        {n}
      </p>
      <p
        className="font-semibold uppercase"
        style={{
          color: "rgba(255,255,255,0.5)",
          fontSize: "11px",
          letterSpacing: "0.14em",
          marginTop: `${sz.gap}px`,
        }}
      >
        {label}
      </p>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// STATE VIEWS — empty, offline, no-events
// ────────────────────────────────────────────────────────────────────────

/** Bannière de synchronisation / offline — palette-safe */
export const SyncBanner = ({ online, error, className = "", testId = "sync-banner" }) => {
  if (online && !error) return null;
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl px-5 py-4 flex items-center gap-3 ${className}`}
      style={{ background: WV.muted, border: `1px solid ${WV.hairline}` }}
    >
      <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ background: WV.ink }}>
        {online ? <WifiOff size={14} className="text-white" /> : <WifiOff size={14} className="text-white" />}
      </div>
      <div>
        <p className="text-[13px] font-semibold" style={{ color: WV.ink }}>
          {online ? "Synchronisation en cours" : "Hors ligne"}
        </p>
        <p className="text-[12px]" style={{ color: WV.textMuted }}>
          Nous reprenons la mise à jour dès que la connexion revient.
        </p>
      </div>
    </div>
  );
};

/** État "Rien aujourd'hui" — card noire accent avec halo lime */
export const EmptyToday = ({ title = "Tout est aligné.", body = "Aucune action urgente. Profitez-en pour préparer demain.", ctaLabel = null, onCta = null, testId = "empty-today" }) => (
  <CardDark pad="p-12 lg:p-16" testId={testId}>
    <div className="max-w-2xl">
      <Upper tone="lime" className="!text-[#C8FF2F]">Aucun événement aujourd&apos;hui</Upper>
      <h2 className="mt-4 text-3xl lg:text-4xl font-semibold tracking-[-0.02em]">{title}</h2>
      <p className="mt-4 text-[15px] text-white/70 leading-relaxed">{body}</p>
      {ctaLabel && (
        <div className="mt-8">
          <BtnLime onClick={onCta}>{ctaLabel}<ArrowRight size={14} strokeWidth={2.5} /></BtnLime>
        </div>
      )}
    </div>
  </CardDark>
);

/** État "Rien à afficher" — softer, card muted */
export const EmptySoft = ({ icon: Icon, title, body, testId = "empty-soft" }) => (
  <CardSoft pad="p-10 lg:p-12" className="text-center" testId={testId}>
    {Icon && (
      <div
        className="mx-auto h-12 w-12 flex items-center justify-center rounded-2xl border"
        style={{ background: WV.limeSoft, borderColor: WV.line }}
      >
        <Icon size={20} strokeWidth={1.8} style={{ color: WV.ink }} />
      </div>
    )}
    <p className="mt-6 text-[16px] font-semibold" style={{ color: WV.ink }}>{title}</p>
    {body && <p className="mt-2 text-[13px] max-w-md mx-auto leading-relaxed" style={{ color: WV.textMuted }}>{body}</p>}
  </CardSoft>
);

// ────────────────────────────────────────────────────────────────────────
// LAYOUT HELPERS
// ────────────────────────────────────────────────────────────────────────

export const Section = ({ children, className = "", testId }) => (
  <section
    data-testid={testId}
    className={`mx-auto max-w-[1400px] px-6 lg:px-10 ${className}`}
  >
    {children}
  </section>
);

/** Séparateur horizontal hairline */
export const Divider = ({ className = "" }) => (
  <div className={`h-px w-full ${className}`} style={{ background: WV.hairline }} />
);
